import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { reminderApi } from 'src/api/reminder';
import { organizationProfileApi } from 'src/api/organization-profile';
import { CreateReminderDto, GetReminderDto, UpdateReminderDto } from 'woofwoof-api';
import { ScopedPage, sliceInMemory } from './scopedPage';
import { useVisitScope } from './useVisitScope';

export type ScopeMutationKind = 'create' | 'update' | 'delete';

interface RemindersScopeOptions {
  page: number;
  rowsPerPage: number;
  /**
   * Mutations only — skip the list query. The Lekarstwa tab creates and edits
   * the reminder linked to a medication, and needs the same profile-or-visit
   * branch without a reminders list of its own.
   */
  skipList?: boolean;
  onMutationSuccess?: (kind: ScopeMutationKind) => void;
  onMutationError?: (kind: ScopeMutationKind) => void;
}

export interface RemindersScope extends ScopedPage<GetReminderDto> {
  isLoading: boolean;
  isVisitScoped: boolean;
  create: (payload: CreateReminderDto) => void;
  update: (reminderId: string, payload: UpdateReminderDto) => void;
  remove: (reminderId: string) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

/**
 * Reminders for the current scope — the patient's profile when there is one,
 * otherwise this visit alone. See `useVisitScope`.
 *
 * The visit branch reuses the query keys `VisitCourseCard` already writes
 * (`['visit-reminders', visitId]`), so Przebieg wizyty and the Przypomnienia
 * tab are two views of one cache rather than two lists that can drift.
 */
export function useRemindersScope({
  page,
  rowsPerPage,
  skipList = false,
  onMutationSuccess,
  onMutationError,
}: RemindersScopeOptions): RemindersScope {
  const { profileId, visitId, isVisitScoped } = useVisitScope();
  const queryClient = useQueryClient();

  const profileQuery = useQuery(
    ['profile-reminders', profileId, page, rowsPerPage],
    () =>
      reminderApi.reminderControllerGetProfileReminders({
        profileId,
        page,
        limit: rowsPerPage,
      }),
    {
      retry: 1,
      enabled: !skipList && !isVisitScoped && Boolean(profileId),
      keepPreviousData: true,
    },
  );

  const visitQuery = useQuery(
    ['visit-reminders', visitId],
    () =>
      organizationProfileApi.organizationProfileControllerListVisitReminders({
        visitId: visitId || '',
      }),
    { retry: 1, enabled: !skipList && isVisitScoped && Boolean(visitId) },
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(['profile-reminders', profileId]);
    if (visitId) {
      queryClient.invalidateQueries(['visit-reminders', visitId]);
      queryClient.invalidateQueries(['visit-activity', visitId]);
    }
  }, [queryClient, profileId, visitId]);

  const { mutate: createMutate, isLoading: isCreating } = useMutation({
    mutationFn: (payload: CreateReminderDto) =>
      isVisitScoped
        ? organizationProfileApi.organizationProfileControllerAddVisitReminder({
            visitId: visitId || '',
            createReminderDto: payload,
          })
        : reminderApi.reminderControllerCreateReminder({
            createReminderDto: payload,
          }),
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('create');
    },
    onError: () => onMutationError?.('create'),
  });

  const { mutate: updateMutate, isLoading: isUpdating } = useMutation({
    mutationFn: ({
      reminderId,
      payload,
    }: {
      reminderId: string;
      payload: UpdateReminderDto;
    }) =>
      isVisitScoped
        ? organizationProfileApi.organizationProfileControllerUpdateVisitReminder(
            {
              visitId: visitId || '',
              reminderId,
              updateReminderDto: payload,
            },
          )
        : reminderApi.reminderControllerUpdateReminder({
            reminderId,
            updateReminderDto: { ...payload, visitId: visitId || undefined },
          }),
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('update');
    },
    onError: () => onMutationError?.('update'),
  });

  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation({
    mutationFn: (reminderId: string) =>
      isVisitScoped
        ? organizationProfileApi.organizationProfileControllerRemoveVisitReminder(
            { visitId: visitId || '', reminderId },
          )
        : reminderApi.reminderControllerDeleteReminder({
            reminderId,
            visitId: visitId || undefined,
          }),
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('delete');
    },
    onError: () => onMutationError?.('delete'),
  });

  const paged: ScopedPage<GetReminderDto> = isVisitScoped
    ? sliceInMemory(visitQuery.data?.data ?? [], page, rowsPerPage)
    : {
        items: profileQuery.data?.data.data ?? [],
        total: profileQuery.data?.data.total ?? 0,
      };

  return {
    ...paged,
    isLoading: isVisitScoped ? visitQuery.isLoading : profileQuery.isLoading,
    isVisitScoped,
    create: createMutate,
    update: useCallback(
      (reminderId: string, payload: UpdateReminderDto) =>
        updateMutate({ reminderId, payload }),
      [updateMutate],
    ),
    remove: deleteMutate,
    isCreating,
    isUpdating,
    isDeleting,
  };
}
